// src/contexts/ProjectContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { projectService } from '../services/project.service';
import { Project } from '../types/project.types';

interface ProjectContextType {
  projects: Project[];
  selectedProjectId: string | null;
  selectedProject: Project | null;
  loading: boolean;
  setSelectedProjectId: (id: string | null) => void;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const LOCAL_STORAGE_KEY = 'canvassed_selected_project';

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(LOCAL_STORAGE_KEY);
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  // Load projects when user changes
  useEffect(() => {
    if (currentUser && userProfile) {
      loadProjects();
    } else {
      setProjects([]);
      setLoading(false);
    }
  }, [currentUser?.uid, userProfile?.role]);

  const loadProjects = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const userProjects = await projectService.getUserProjects(currentUser.uid);
      // Filter to only active projects
      const activeProjects = userProjects.filter(p => p.status === 'active');
      setProjects(activeProjects);

      // If no project selected or selected project not in list, select first
      if (activeProjects.length > 0) {
        const savedId = localStorage.getItem(LOCAL_STORAGE_KEY);
        const validSavedProject = activeProjects.find(p => p.id === savedId);
        if (!validSavedProject) {
          setSelectedProjectIdState(activeProjects[0].id);
          localStorage.setItem(LOCAL_STORAGE_KEY, activeProjects[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const setSelectedProjectId = useCallback((id: string | null) => {
    setSelectedProjectIdState(id);
    if (id) {
      localStorage.setItem(LOCAL_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [currentUser?.uid]);

  const selectedProject = projects.find(p => p.id === selectedProjectId) || null;

  const value: ProjectContextType = {
    projects,
    selectedProjectId,
    selectedProject,
    loading,
    setSelectedProjectId,
    refreshProjects
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;
